/**
 * 마스터플랜 (/adm/project/master)
 * ($j.Pages.Admin.MasterPlan / body data-page-name="MasterPlan")
 *
 *   ① 요약 — 전체·지연·기한 초과·이번 주 마감·오픈까지
 *   ② 캘린더 — 공용 모듈 $j.F.Admin.MasterCalendar (주간업무보고서도 같은 것을 쓴다).
 *      색은 기본 프로젝트별 — 작업에 원장 프로젝트를 붙이면 그 프로젝트의 기획·개발·테스트·오픈이 한 색
 *   ③ 프로젝트 — 프로젝트 원장(g5_projects) 관리. 일정이 없는 프로젝트도 전부 나오고, 등록·수정·삭제·상위 관리가 여기 있다.
 *      프로젝트마다 '캘린더에서 숨기기'(pj_cal_hidden)를 켜면 캘린더(이 화면·주간업무보고서)에서만 빠진다
 *   ④ 작업 목록 — 기준/현재 일정, 지연 영업일, 상태·진행률
 *   ⑤ 일일 업데이트 — 오늘 한 일·이슈·상태·진행률, 필요하면 일정 변경(사유 필수)
 *   작업·프로젝트 추가·수정은 우측 시트($j.Ui.Modal right). 저장 후에는 서버 값을 다시 불러온다.
 *
 * 지연 일수는 영업일(주말·공휴일 제외) — 서버 MasterPlanService::bizDiff 와 같은 셈.
 * 여기서 쓴 내용은 주간업무보고 '마스터플랜' 섹션(WeeklyMarkdown)이 그 주 기준으로 가져간다.
 */
import Util from '../../Common/Util';

const DOWS = '일월화수목금토';
const pad = n => String(n).padStart(2, '0');
const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const utc = s => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); };
const diff = (a, b) => Math.round((utc(b) - utc(a)) / 864e5);
const addD = (s, n) => { const d = parse(s); d.setDate(d.getDate() + n); return iso(d); };
const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmt = s => { if (!s) return '-'; const d = parse(s); return `${d.getMonth() + 1}/${d.getDate()}(${DOWS[d.getDay()]})`; };
const md = s => { const d = parse(s); return `${d.getMonth() + 1}/${d.getDate()}`; };

const LOG_TYPE = { daily: '일일', edit: '수정', reschedule: '일정 변경', rebaseline: '기준 변경', create: '등록', status: '상태' };

export default class MasterPlan {
    initialize() {
        this.tasks = [];
        this.pjBars = [];   // 작업 없는 프로젝트 — 캘린더에만 원장 기간으로 (MasterPlanService::projectBars)
        this.logs = [];
        this.members = [];
        this.projects = [];
        this.groups = [];
        this.PH = {};
        this.statuses = [];
        this.hol = {};
        this.today = iso(new Date());
        this.view = 'calendar';
        // range — 작업 목록 범위. 기본 '이번 주 기준'(프로젝트 보기와 같은 셈: 안 끝난 것 전부 + 최근 2주 안에 끝난 것)
        this.f = { owner: 0, phase: '', project: 0, late: false, showHidden: false, range: 'week', hideDone: false };
        this.pf = { status: 'week', keyword: '', showEmpty: false };   // 프로젝트 보기 필터 — ProjectService::list 의 status 값
        this.pjRows = [];
        this._pjSeq = 0;
        this.colorBy = 'project';
        this.feedAll = false;
        this.sheet = null;
        try {
            this.view = localStorage.getItem('mp.view') || this.view;
            this.colorBy = localStorage.getItem('mp.color2') || this.colorBy;
            this.f.hideDone = localStorage.getItem('mp.hideDone') === '1';
        } catch (e) { /* 저장소 막힘 — 기본값 */ }
        // 옛 '프로젝트 관리' 주소(/adm/project)는 ?v=projects 로 넘어온다
        const qv = new URLSearchParams(location.search).get('v');
        if (qv) this.view = qv;
        if (!['calendar', 'projects', 'list', 'daily'].includes(this.view)) this.view = 'calendar';

        this._bind();
        this._load();
    }

    /* ── 로드 ───────────────────────────────────────── */

    async _load() {
        try {
            const res = await $.post('/adm/ajax/project/master/data');
            if (!res.result) return Util.swalert(res.message || '불러오지 못했습니다.');
            this.tasks = res.tasks || [];
            this.pjBars = res.pjBars || [];
            this.logs = res.logs || [];
            this.members = res.members || [];
            this.projects = res.projects || [];
            this.groups = res.groups || [];
            this.PH = res.phases || {};
            this.statuses = res.statuses || [];
            this.hol = res.holidays || {};
            if (res.today) this.today = res.today;
            this._render();
        } catch (e) {
            Util.swalert('불러오지 못했습니다.');
        }
    }

    /* ── 계산 ───────────────────────────────────────── */

    _isBiz(s) { const w = parse(s).getDay(); return w !== 0 && w !== 6 && !this.hol[s]; }

    _bdiff(a, b) {
        if (!a || !b || a === b) return 0;
        const sg = b > a ? 1 : -1;
        let n = 0, c = a;
        while (c !== b) { c = addD(c, sg); if (this._isBiz(c)) n++; }
        return n * sg;
    }

    _isMsPhases(arr) { return arr.length > 0 && arr.every(p => this.PH[p]?.kind === 'ms'); }
    _primary(t) { return t.phases.find(p => this.PH[p]?.kind === 'range') || t.phases[0] || 'etc'; }
    _phaseLbl(t) { return t.phases.map(p => this.PH[p]?.label || '').join('·'); }
    _byId(id) { return this.tasks.find(t => t.num === +id); }
    _range(t) { return t.is_ms ? fmt(t.mt_cur_end) : `${fmt(t.mt_cur_start)} ~ ${fmt(t.mt_cur_end)}`; }
    _baseRange(t) { return t.is_ms ? fmt(t.mt_plan_end) : `${fmt(t.mt_plan_start)} ~ ${fmt(t.mt_plan_end)}`; }
    _ownerTxt(t) { return t.owners.map(o => o.name).join(', ') || '미지정'; }

    _info(t) {
        const delay = this._bdiff(t.mt_plan_end, t.mt_cur_end);
        const over = t.mt_status !== '완료' && t.mt_cur_end < this.today;
        const dep = t.deps.map(id => this._byId(id)).filter(d => d && d.mt_status !== '완료' && d.mt_cur_end >= t.mt_cur_start);
        return { delay, over, dep };
    }

    /** 주간보고 주차와 같은 토~금 */
    _reportWeek(s) { const w = parse(s).getDay(); const sat = addD(s, -((w + 1) % 7)); return [sat, addD(sat, 6)]; }

    /** @param forCal 캘린더용 — 원장에서 숨긴 프로젝트를 뺀다(체크하면 보인다) */
    _visible(forCal = false) {
        return (forCal ? this.tasks.concat(this.pjBars) : this.tasks).filter(t => {
            if (forCal && t.cal_hidden && !this.f.showHidden) return false;
            if (forCal && this.f.hideDone && t.mt_status === '완료') return false;
            if (this.f.owner && !t.owners.some(o => o.num === this.f.owner)) return false;
            if (this.f.phase && !t.phases.includes(this.f.phase)) return false;   // 프로젝트 막대는 단계가 없어 빠진다
            if (this.f.project && (t.pj_num || -1) !== this.f.project) return false;
            if (this.f.late) { const i = this._info(t); if (!(i.delay > 0 || i.over)) return false; }
            return true;
        });
    }

    /* ── 표기 ───────────────────────────────────────── */

    /** 담당자 파스텔 — 주간보고(Weekly.js·WeeklyMarkdown)와 같은 색 */
    _pastel(name) {
        const map = { '홍': ['#ECE3FB', '#6A3FA0'], '박': ['#E1ECFB', '#2F5C9E'], '유': ['#E2F3E4', '#37773F'], '윤': ['#FBEFD6', '#8A6612'] };
        return map[String(name ?? '').trim().charAt(0)] || ['#ECEEF1', '#5A6270'];
    }
    /** 사람 배지 — 흰 바탕 + 이름 글자색만 사람별 (캘린더 MasterCalendar 와 같은 모양) */
    _ob(name) { return `<span class="ob" style="background:#fff;color:${this._pastel(name)[1]};box-shadow:inset 0 0 0 1px var(--adm-border)">${esc(name)}</span>`; }
    _people(t) { return t.owners.map(o => this._ob(o.name)).join(''); }

    _flags(t, short) {
        const i = this._info(t);
        let h = '';
        const was = short ? md(t.mt_plan_end) : fmt(t.mt_plan_end);
        if (i.delay > 0) h += `<span class="chip late" title="기준 ${esc(this._baseRange(t))} → 현재 ${esc(this._range(t))} (주말·공휴일 제외)">+${i.delay}일 지연 · 원래 ${was}</span>`;
        else if (i.delay < 0) h += `<span class="chip early">${-i.delay}일 단축 · 원래 ${was}</span>`;
        if (i.over) h += '<span class="chip over">기한 초과</span>';
        if (i.dep.length && !short) h += `<span class="chip dep" title="${esc(i.dep.map(d => d.mt_title).join(', '))}">선행작업 영향</span>`;
        return h;
    }

    /* ── 렌더 ───────────────────────────────────────── */

    _render() {
        $('#mpViews [data-v]').each((_, b) => $(b).toggleClass('active btn-secondary', b.dataset.v === this.view).toggleClass('btn-default', b.dataset.v !== this.view));
        this._renderStats();
        this._renderFilters();
        const v = this.view;
        if (v === 'calendar') {
            // 다시 그려도 보던 달을 유지하도록 캘린더 자리는 남겨 둔다
            if (!$('#mpCal').length) $('#mpView').html('<div id="mpCal"></div>');
            this._mountCalendar();
        } else if (v === 'projects') {
            if (!$('#pjTable').length) $('#mpView').html(this._projectsShell());
            this._loadProjects();
        } else {
            $('#mpView').html(v === 'list' ? this._renderList() : this._renderDaily());
        }
        if (v === 'daily') this._bindDaily();
    }

    _renderStats() {
        const all = this.tasks;
        const [ws, we] = this._reportWeek(this.today);
        const done = all.filter(t => t.mt_status === '완료').length;
        const late = all.filter(t => this._info(t).delay > 0);
        const maxLate = late.reduce((m, t) => Math.max(m, this._info(t).delay), 0);
        const over = all.filter(t => this._info(t).over).length;
        const thisWeek = all.filter(t => t.mt_status !== '완료' && t.mt_cur_end >= ws && t.mt_cur_end <= we).length;
        const opens = all.filter(t => t.phases.includes('open')).sort((a, b) => (a.mt_cur_end < b.mt_cur_end ? -1 : 1));
        const op = opens.find(t => t.mt_cur_end >= this.today) || opens[opens.length - 1];
        let opV = '-', opD = '오픈 일정을 등록하세요';
        if (op) {
            const dd = diff(this.today, op.mt_cur_end);
            opV = dd > 0 ? `D-${dd}` : dd === 0 ? 'D-DAY' : `D+${-dd}`;
            const sl = this._bdiff(op.mt_plan_end, op.mt_cur_end);
            opD = `${op.mt_title} ${fmt(op.mt_cur_end)}${sl > 0 ? ` · 기준 대비 +${sl}일` : ''}`;
        }
        const issues = all.filter(t => t.mt_status === '이슈').length;
        $('#mpStats').html(`
            <div class="st-c"><div class="k">전체 작업</div><div class="v">${all.length}<small> 건</small></div><div class="d">완료 ${done}건 · 이슈 ${issues}건</div></div>
            <div class="st-c ${late.length ? 'alert-c' : ''}"><div class="k">지연 작업</div><div class="v">${late.length}<small> 건</small></div><div class="d">${late.length ? `최대 +${maxLate}일` : '기준 일정대로 진행 중'}</div></div>
            <div class="st-c ${over ? 'alert-c' : ''}"><div class="k">기한 초과</div><div class="v">${over}<small> 건</small></div><div class="d">완료 처리 안 된 지난 작업</div></div>
            <div class="st-c"><div class="k">이번 주 마감</div><div class="v">${thisWeek}<small> 건</small></div><div class="d">${fmt(ws)} ~ ${fmt(we)}</div></div>
            <div class="st-c"><div class="k">가까운 오픈</div><div class="v">${opV}</div><div class="d" title="${esc(opD)}">${esc(opD)}</div></div>`);
    }

    _renderFilters() {
        $('#mpFOwner').html('<option value="0">담당자 전체</option>' + this.members.map(m =>
            `<option value="${m.num}" ${+m.num === this.f.owner ? 'selected' : ''}>${esc(m.manage_name)}</option>`).join(''));
        $('#mpFPhase').html('<option value="">구분 전체</option>' + Object.entries(this.PH).map(([k, v]) =>
            `<option value="${k}" ${k === this.f.phase ? 'selected' : ''}>${v.label}</option>`).join(''));
        // 프로젝트 필터는 작업이 실제로 붙은 프로젝트만 (원장 전체를 늘어놓으면 고르기 어렵다)
        const used = [...new Map(this.tasks.filter(t => t.pj_num).map(t => [t.pj_num, t.pj_name])).entries()];
        $('#mpFProject').html('<option value="0">프로젝트 전체</option>'
            + used.map(([n, name]) => `<option value="${n}" ${n === this.f.project ? 'selected' : ''}>${esc(name)}</option>`).join('')
            + (this.tasks.some(t => !t.pj_num) ? `<option value="-1" ${this.f.project === -1 ? 'selected' : ''}>프로젝트 없음</option>` : ''));
        $('#mpFRange').val(this.f.range).toggle(this.view === 'list');
        $('#mpFLate').prop('checked', this.f.late);
        $('#mpFHidden').prop('checked', this.f.showHidden);
        $('#mpFDone').prop('checked', this.f.hideDone);
        $('#mpFDoneWrap').toggle(this.view === 'calendar');
        $('#mpFilters').toggle(this.view !== 'daily');
        // 프로젝트 보기는 담당자만 거른다 — 구분·지연은 작업(일정)에 붙은 값이다
        $('#mpFProject, #mpFPhase').toggle(this.view !== 'projects');
        $('#mpFLate').closest('label').toggle(this.view !== 'projects');
        const hidden = this.tasks.concat(this.pjBars).filter(t => t.cal_hidden).length;
        $('#mpFHiddenWrap').toggle(this.view === 'calendar' && hidden > 0);
    }

    _emptyState() {
        return `<div class="mp-empty"><div style="font-weight:700;font-size:15px;color:var(--adm-text-heading)">아직 등록된 작업이 없습니다</div>
            <div>날짜 숫자를 누르거나 [작업 추가]로 기획·개발·테스트·개발마감 기간과 오픈 일정을 넣으세요.</div></div>`;
    }

    /** 캘린더는 공용 모듈이 그린다 — 주간업무보고서도 같은 것을 쓴다 */
    _mountCalendar() {
        $j.F.Admin.MasterCalendar.mount($('#mpCal')[0], {
            tasks: this._visible(true), phases: this.PH, holidays: this.hol, today: this.today,
            colorBy: this.colorBy,
            emptyHtml: this.tasks.length ? '조건에 맞는 작업이 없습니다.' : '아직 등록된 작업이 없습니다. 날짜 숫자를 누르거나 [작업 추가]로 넣으세요.',
            onColor: (v) => { this.colorBy = v; try { localStorage.setItem('mp.color2', v); } catch (_) { /* 무시 */ } },
            onOpen: (num) => (num < 0 ? this._openProjectSheet(-num) : this._openSheet(num)),   // 음수 = 작업 없는 프로젝트
            onNew: (date) => this._openSheet(null, date),
        });
    }

    /** 작업 목록 범위 — 완료는 상태값으로 가른다. 최근 = 끝난 지 WEEK_DONE_DAYS(14일) 안 */
    _inRange(t) {
        const done = t.mt_status === '완료';
        switch (this.f.range) {
            case 'week': return !done || t.mt_cur_end >= addD(this.today, -14);
            case 'open': return !done;
            case 'done': return done;
            default:     return true;
        }
    }

    _renderList() {
        if (!this.tasks.length) return this._emptyState();
        const list = this._visible().filter(t => this._inRange(t)).sort((a, b) => (a.mt_cur_start < b.mt_cur_start ? -1 : a.mt_cur_start > b.mt_cur_start ? 1 : 0));
        const rows = list.map(t => {
            const i = this._info(t);
            const changed = t.mt_plan_start !== t.mt_cur_start || t.mt_plan_end !== t.mt_cur_end;
            return `<tr data-id="${t.num}">
                <td>${t.pg_name ? `<div class="hint">${esc(t.pg_name)}</div>` : ''}<b>${esc(t.mt_title)}</b>${t.mt_delay_reason && i.delay > 0 ? `<div class="hint">사유: ${esc(t.mt_delay_reason)}</div>` : ''}</td>
                <td>${t.phases.map(p => `<div><span class="ph-tag ph-${p}">${this.PH[p]?.label || ''}</span></div>`).join('')}</td>
                <td><span class="ppl">${this._people(t) || '<span class="hint">미지정</span>'}</span></td>
                <td>${this._baseRange(t)}</td>
                <td>${changed ? `<b>${this._range(t)}</b>` : this._range(t)}</td>
                <td>${this._flags(t) || '<span class="hint">-</span>'}</td>
                <td><span class="stt stt-${esc(t.mt_status)}">${esc(t.mt_status)}</span></td>
                <td><div class="prog"><div class="track"><div class="fill" style="width:${t.mt_progress}%"></div></div><span>${t.mt_progress}%</span></div></td>
            </tr>`;
        }).join('');
        return `<div class="table-responsive"><table class="table table-hover mp-tbl">
            <thead><tr><th>프로젝트 · 작업</th><th>구분</th><th>담당자</th><th>기준 일정</th><th>현재 일정</th><th>지연</th><th>상태</th><th>진행률</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="8" class="text-center hint py-4">조건에 맞는 작업이 없습니다.</td></tr>'}</tbody></table></div>`;
    }

    /* ── 프로젝트 보기 (원장) ───────────────────────── */

    _projectsShell() {
        const st = [['week', '이번 주 기준'], ['running', '진행중'], ['done', '완료'], ['보류', '보류'], ['', '전체']];
        return `<div class="pj-bar">
                <select class="form-control form-control-sm" id="pjFStatus" aria-label="상태">${st.map(([v, l]) => `<option value="${v}" ${v === this.pf.status ? 'selected' : ''}>${l}</option>`).join('')}</select>
                <input class="form-control form-control-sm" id="pjFKeyword" placeholder="프로젝트명·메모 검색" value="${esc(this.pf.keyword)}" style="width:220px">
                <span class="hint">이번 주 기준 = 진행중·보류 전부 + 최근 2주 안에 끝난 것</span>
                <label class="mp-chk mb-0"><input type="checkbox" id="pjFEmpty" ${this.pf.showEmpty ? 'checked' : ''}> 일정 없는 프로젝트도 보기 <span class="hint" id="pjEmptyCnt"></span></label>
                <button type="button" class="btn btn-default btn-sm ml-auto" data-act="pjGroups"><i class="fas fa-layer-group"></i> 상위 목록</button>
            </div>
            <div class="table-responsive" id="pjTable"><div class="mp-empty">불러오는 중…</div></div>`;
    }

    async _loadProjects() {
        const seq = ++this._pjSeq;
        try {
            const r = await $.post('/adm/ajax/project/list', { status: this.pf.status, keyword: this.pf.keyword, manage_num: this.f.owner || '' });
            if (seq !== this._pjSeq) return;   // 그 사이 필터를 또 바꿨다
            this.pjRows = r.data || [];
            $('#pjTable').html(this._renderProjects());
        } catch (e) {
            if (seq === this._pjSeq) $('#pjTable').html('<div class="mp-empty">불러오지 못했습니다.</div>');
        }
    }

    _pjTasks(pjNum) {
        return this.tasks.filter(t => t.pj_num === pjNum).sort((a, b) => (a.mt_cur_start < b.mt_cur_start ? -1 : a.mt_cur_start > b.mt_cur_start ? 1 : 0));
    }

    /** 일정 한 줄 — [개발] 모바일 화면 10/14~10/16 40% +2일 */
    _taskLine(t) {
        const r = t.is_ms ? md(t.mt_cur_end) : `${md(t.mt_cur_start)}~${md(t.mt_cur_end)}`;
        const tag = t.phases.map(p => this.PH[p]?.short || '').join('·');
        const name = t.mt_name ? `<span>${esc(t.mt_name)}</span>` : '';   // 작업명 — 같은 단계 갈래를 가른다
        return `<span class="ph-tag ph-${this._primary(t)}">${esc(tag)}</span>${name}<span>${r}</span>${t.mt_status === '완료' ? '<span class="stt stt-완료">완료</span>' : `<span class="stt stt-pct">${t.mt_progress}%</span>${this._flags(t, true)}`}`;
    }

    /**
     * 일정(마스터플랜 작업)이 하나도 없는 프로젝트는 기본으로 뺀다 — 이름만 걸어둔 것이 목록을 덮는다.
     * 상위 머리줄은 남은 하위로 다시 센다 (서버 집계는 빠진 것까지 센다).
     */
    _visibleProjects() {
        const empty = this.pjRows.filter(r => !r._is_group && !this._pjTasks(r.num).length).length;
        $('#pjEmptyCnt').text(empty ? `(${empty}건)` : '');
        if (this.pf.showEmpty) return this.pjRows;
        const out = [];
        let head = null, kids = [];
        const flush = () => {
            if (head && kids.length) {
                out.push({ ...head, _member_count: kids.length, pj_progress: Math.round(kids.reduce((s, k) => s + (+k.pj_progress || 0), 0) / kids.length) });
            }
            if (!head || kids.length) out.push(...kids);
            head = null; kids = [];
        };
        this.pjRows.forEach(r => {
            if (r._is_group) { flush(); head = r; return; }
            if (!r._depth && head) flush();   // 미분류는 상위 밑이 아니다
            if (this._pjTasks(r.num).length) kids.push(r);
        });
        flush();
        return out;
    }

    _renderProjects() {
        const list = this._visibleProjects();
        if (!list.length) {
            const filtered = this.pf.status || this.pf.keyword || this.f.owner;
            const msg = this.pjRows.length ? '일정이 잡힌 프로젝트가 없습니다. 「일정 없는 프로젝트도 보기」를 켜면 나옵니다.'
                : filtered ? '조건에 맞는 프로젝트가 없습니다.' : '등록된 프로젝트가 없습니다. [프로젝트 등록]으로 넣으세요.';
            return `<div class="mp-empty">${msg}</div>`;
        }
        const rows = list.map(r => {
            if (r._is_group) {
                return `<tr class="pj-g"><td colspan="5"><i class="fas fa-layer-group"></i> ${esc(r.pj_name)} <span class="hint" style="font-weight:400">하위 ${r._member_count || 0} · 평균 ${r.pj_progress}%</span></td></tr>`;
            }
            const tasks = this._pjTasks(r.num);
            const done = r._is_done, hold = r.pj_status === '보류';
            const badge = hold ? ' <span class="stt stt-보류">보류</span>' : done ? ' <span class="stt stt-완료">완료</span>' : '';
            const owners = String(r.owner_names || '').split(',').filter(Boolean).map(n => this._ob(n)).join('');
            const sched = tasks.length
                ? `<div class="pj-sched">${tasks.map(t => `<div data-task="${t.num}" title="작업 고치기">${this._taskLine(t)}</div>`).join('')}</div>`
                : `<span class="hint">일정 없음</span> <button type="button" class="btn btn-default btn-xs" data-act="pjAddTask" data-pj="${r.num}"><i class="fas fa-plus"></i> 일정 추가</button>`;
            const hid = +r.pj_cal_hidden === 1;
            const cal = `<button type="button" class="btn btn-xs ${hid ? 'btn-default' : 'btn-outline-secondary'}" data-act="pjHide" data-pj="${r.num}" data-hidden="${hid ? 1 : 0}" title="${hid ? '캘린더에서 숨김 — 누르면 다시 보입니다' : '캘린더에 표시 중 — 누르면 숨깁니다'}">`
                + `<i class="fas ${hid ? 'fa-eye-slash' : 'fa-eye'}"></i> ${hid ? '숨김' : '표시'}</button>`;
            return `<tr data-pj="${r.num}" class="${done ? 'is-done' : ''} ${hold ? 'is-hold' : ''} ${hid ? 'pj-off' : ''}">
                <td>${r._depth ? '<span class="ind">└</span>' : ''}<b class="nm">${esc(r.pj_name)}</b>${badge}${r._has_memo ? ' <i class="fas fa-sticky-note hint" title="메모 있음"></i>' : ''}</td>
                <td><span class="ppl">${owners || '<span class="hint">미지정</span>'}</span></td>
                <td>${sched}</td>
                <td><div class="prog"><div class="track"><div class="fill" style="width:${r.pj_progress}%"></div></div><span>${r.pj_progress}%</span></div></td>
                <td class="text-center text-nowrap">${cal}</td>
            </tr>`;
        }).join('');
        return `<table class="table table-hover pj-tbl">
            <thead><tr><th>프로젝트</th><th style="width:150px">담당</th><th>마스터플랜 일정</th><th style="width:130px">진행률</th><th style="width:80px" class="text-center">캘린더</th></tr></thead>
            <tbody>${rows}</tbody></table>`;
    }

    async _toggleHidden(num, hidden) {
        try {
            const r = await $.post('/adm/ajax/project/edit-field', { num, field: 'pj_cal_hidden', value: hidden ? 1 : 0 });
            if (!r.result) return Util.swalert(r.message || '저장하지 못했습니다.');
            $j.Ui.Toast.show(hidden ? '캘린더에서 숨겼습니다' : '캘린더에 다시 보입니다');
            this._load();
        } catch (e) {
            Util.swalert('저장하지 못했습니다.');
        }
    }

    /* ── 프로젝트 시트 (원장 등록·수정) ────────────── */

    async _openProjectSheet(num) {
        let row = { num: 0, pj_name: '', pj_group: null, pj_status: '진행중', pj_progress: 0, owner_nums: [], pj_cal_hidden: 0 };
        if (num) {
            try {
                const r = await $.post('/adm/ajax/project/detail', { num });
                if (!r.result) return Util.swalert(r.message || '불러오지 못했습니다.');
                row = r.data;
                if (r.groups) this.groups = r.groups;
            } catch (e) {
                return Util.swalert('불러오지 못했습니다.');
            }
        }
        const isNew = !row.num;
        const gName = (this.groups.find(g => g.num === row.pj_group) || {}).pg_name || '';
        const tasks = isNew ? [] : this._pjTasks(row.num);
        const d10 = v => String(v || '').substring(0, 10);

        const html = `<form id="mpPjForm" class="mp-sheet" novalidate>
            <div class="form-group"><label for="pName">프로젝트명 <span class="req">*</span></label><input class="form-control form-control-sm" id="pName" value="${esc(row.pj_name)}"></div>
            <div class="form-group"><label for="pGroup">상위 프로젝트 <span class="hint">비워도 됩니다 · 없는 이름이면 새로 만듭니다</span></label>
                <input class="form-control form-control-sm" id="pGroup" value="${esc(gName)}" placeholder="없음 (미분류)" autocomplete="off"></div>
            <div class="form-group"><label>담당자</label>
                <div class="seg">${this.members.map(m => `<label><input type="checkbox" name="pOwners" value="${m.num}" ${(row.owner_nums || []).includes(+m.num) ? 'checked' : ''}>${this._ob(m.manage_name)}</label>`).join('')}</div></div>
            <div class="form-row">
                <div class="form-group col-4"><label for="pStatus">상태</label><select class="form-control form-control-sm" id="pStatus">${['진행중', '보류'].map(v => `<option ${v === row.pj_status ? 'selected' : ''}>${v}</option>`).join('')}</select></div>
                <div class="form-group col-4"><label for="pProg">진행률(%)</label><input class="form-control form-control-sm" type="number" min="0" max="100" id="pProg" value="${parseInt(row.pj_progress) || 0}" ${tasks.length ? 'disabled title="작업 진행률에서 계산됩니다"' : ''}></div>
                <div class="form-group col-4"><label for="pDone">실제종료일</label><input class="form-control form-control-sm" type="date" id="pDone" value="${d10(row.pj_completed)}"></div>
            </div>
            <div class="hint" style="margin:-6px 0 10px">${tasks.length ? '진행률은 아래 작업들의 진행률로 계산됩니다(기간이 긴 작업일수록 무겁게). 작업이 모두 완료되면 100이 되고 실제종료일이 찍힙니다.' : '진행률 100이면 완료로 처리되고 실제종료일이 자동으로 찍힙니다. 작업을 넣으면 작업 진행률로 계산됩니다.'}</div>
            <div class="form-row">
                <div class="form-group col-6"><label for="pStart">시작일</label><input class="form-control form-control-sm" type="date" id="pStart" value="${d10(row.pj_started)}"></div>
                <div class="form-group col-6"><label for="pDue">예상종료일</label><input class="form-control form-control-sm" type="date" id="pDue" value="${d10(row.pj_due)}"></div>
            </div>
            ${isNew ? this._baseTasksBox() : ''}
            <label class="mp-chk mb-3"><input type="checkbox" id="pHidden" ${+row.pj_cal_hidden === 1 ? 'checked' : ''}> 캘린더에서 숨기기 <span class="hint">작업 목록·보고서 표에는 남습니다</span></label>
            <div class="form-group"><label for="pMemo">메모 <span class="hint">내부 기록 · 보고서에 나가지 않습니다</span></label><textarea class="form-control form-control-sm" id="pMemo" rows="4" placeholder="진행 경위, 결정사항, 막힌 지점 등">${esc(row.pj_memo || '')}</textarea></div>
            ${isNew ? '' : `<div class="box pj-tasks"><div class="bt">마스터플랜 일정 <span class="hint" style="font-weight:400">${tasks.length ? tasks.length + '건 · 누르면 일정을 고칩니다' : '아직 없습니다'}</span>
                    <button type="button" class="btn btn-default btn-xs ml-auto" id="pTaskAdd"><i class="fas fa-plus"></i> 일정 추가</button></div>
                    ${tasks.map(t => `<div class="pj-task" data-task="${t.num}">${this._taskLine(t)}<span class="ppl ml-auto">${this._people(t)}</span></div>`).join('')}</div>
                <details class="form-group mp-hist" id="pWkWrap"><summary>최근 주간보고 기록</summary><div class="mt-2" id="pWk"><div class="hint">불러오는 중…</div></div></details>`}
            <div class="err" id="pErr" role="alert"></div>
            ${isNew ? '' : `<div class="box" id="pDelBox" style="display:none"><div class="hint mb-1">되돌릴 수 없습니다. 지우려면 <b>${esc(row.pj_name)}</b> 를 그대로 입력하세요.</div>
                <div class="d-flex" style="gap:6px"><input class="form-control form-control-sm" id="pDelName" autocomplete="off"><button type="button" class="btn btn-danger btn-sm text-nowrap" id="pDelGo" disabled>영구 삭제</button></div></div>
            <div class="box" id="pMrgBox" style="display:none"><div class="hint mb-1"><b>${esc(row.pj_name)}</b> 를 아래 프로젝트에 합칩니다. 일정·주간보고 기록·담당자·메모가 옮겨가고 이 프로젝트는 없어집니다.</div>
                <div class="d-flex" style="gap:6px"><select class="form-control form-control-sm" id="pMrgTo" aria-label="합칠 대상"><option value="">불러오는 중…</option></select><button type="button" class="btn btn-warning btn-sm text-nowrap" id="pMrgGo" disabled>합치기</button></div></div>`}
            <div class="foot">
                ${isNew ? '<span></span>' : '<div><button type="button" class="btn btn-outline-danger btn-sm" id="pDel">삭제</button> <button type="button" class="btn btn-default btn-sm" id="pMrg">다른 프로젝트에 합치기</button></div>'}
                <div><button type="button" class="btn btn-default btn-sm" id="pClose">닫기</button> <button type="submit" class="btn btn-primary btn-sm">저장</button></div>
            </div>
            ${isNew ? '' : `<div class="hint mt-2">코드 ${esc(row.pj_code || '')} · 등록 ${esc(d10(row.created_at))}</div>`}
        </form>`;

        const sheet = $j.Ui.Modal.open({ id: 'mpPjSheet', title: isNew ? '프로젝트 등록' : '프로젝트 수정', html, mode: 'right', width: '540px' });
        const $f = $('#mpPjForm');
        $('#pClose').on('click', () => sheet.close());
        $('#pTaskAdd').on('click', () => { sheet.close(); this._ensureProjectOption(row); this._openSheet(null, null, row.num); });
        $f.on('click', '[data-task]', (e) => { sheet.close(); this._openSheet(+e.currentTarget.dataset.task); });
        $('#pWkWrap').one('toggle', () => this._loadProjectWeekly(row.num));
        $('#pDel').on('click', () => { $('#pMrgBox').hide(); $('#pDelBox').toggle(); $('#pDelName').val('').trigger('focus'); });
        $('#pMrg').on('click', () => { $('#pDelBox').hide(); $('#pMrgBox').toggle(); this._fillMergeTargets(row); });
        $('#pMrgTo').on('change', (e) => $('#pMrgGo').prop('disabled', !e.target.value));
        $('#pMrgGo').on('click', () => this._mergeProject(row, sheet));
        $('#pDelName').on('input', (e) => $('#pDelGo').prop('disabled', e.target.value.trim() !== String(row.pj_name).trim()));
        $('#pDelGo').on('click', async () => {
            try {
                const r = await $.post('/adm/ajax/project/delete', { num: row.num, confirm_name: $('#pDelName').val() });
                if (!r.result) return $('#pErr').text(r.message || '삭제하지 못했습니다.');
                sheet.close();
                $j.Ui.Toast.show('프로젝트를 삭제했습니다');
                this._load();
            } catch (e) {
                $('#pErr').text('삭제하지 못했습니다.');
            }
        });
        $f.on('submit', (e) => { e.preventDefault(); this._saveProject(row, sheet); });
        this._groupCombo('#pGroup');
        if (isNew) this._bindBaseTasks();
        setTimeout(() => $('#pName').trigger('focus'), 30);
    }

    /**
     * 신규 등록 — 기본 작업(단계별 일정)을 같이 만든다. [+기획]·[+개발]… 로 줄을 늘리고, 같은 단계도 작업명으로 여러 줄 둘 수 있다.
     * 기타 일정(하루짜리)은 뺀다 — 작업 시트에서 넣는다.
     */
    _baseTasksBox() {
        const adds = Object.entries(this.PH).filter(([k]) => k !== 'etc')
            .map(([k, v]) => `<button type="button" class="btn btn-default btn-xs" data-pb-add="${k}"><i class="fas fa-plus"></i> <span class="ph-tag ph-${k}">${esc(v.short)}</span></button>`).join('');
        return `<div class="box pj-base"><div class="bt">기본 작업 <span class="hint" style="font-weight:400">캘린더 일정으로 같이 만들어집니다 · 날짜는 시작일~예상종료일을 단계별로 나눠 채웁니다</span></div>
            <div class="pb-list"></div><div class="pb-adds">${adds}</div></div>`;
    }

    _bindBaseTasks() {
        const $box = $('#mpPjForm .pj-base');
        const order = Object.keys(this.PH);
        // 손대지 않은 칸만 다시 채운다 — 기획·개발·테스트·마감은 단계 수로 기간을 나눠 차례로(같은 단계 여러 줄은 같은 구간),
        // 기타 작업은 전체 기간, 하루짜리(오픈)는 예상종료일
        const fill = () => {
            const S = $('#pStart').val(), E = $('#pDue').val() || S;
            if (!S || E < S) return;
            const $rows = $box.find('.pb-row');
            const seq = [...new Set($rows.get().map(r => r.dataset.ph))].filter(k => this.PH[k]?.kind === 'range' && k !== 'work')
                .sort((a, b) => order.indexOf(a) - order.indexOf(b));
            const n = Math.round((parse(E) - parse(S)) / 864e5) + 1, k = seq.length;
            const seg = i => [addD(S, Math.floor(i * n / k)), addD(S, Math.max(Math.floor(i * n / k), Math.floor((i + 1) * n / k) - 1))];
            $rows.each((_, r) => {
                const ph = r.dataset.ph;
                const [a, b] = this.PH[ph]?.kind === 'ms' ? [E, E] : ph === 'work' ? [S, E] : seg(seq.indexOf(ph));
                const $s = $(r).find('.pb-s'), $e = $(r).find('.pb-e');
                if (!$s.data('touched')) $s.val(a);
                if ($e.length && !$e.data('touched')) $e.val(b);
            });
        };
        $box.on('click', '[data-pb-add]', (e) => {
            const k = e.currentTarget.dataset.pbAdd, v = this.PH[k], ms = v.kind === 'ms';
            const $r = $(`<div class="pb-row" data-ph="${k}">
                <span class="ph-tag ph-${k}">${esc(v.short)}</span>
                <input class="form-control form-control-sm pb-n" maxlength="100" placeholder="작업명 (비워도 됨)" aria-label="${esc(v.label)} 작업명">
                <input type="date" class="form-control form-control-sm pb-s" aria-label="${esc(v.label)} ${ms ? '날짜' : '시작일'}">
                ${ms ? '<span class="pb-gap"></span>' : `<span>~</span><input type="date" class="form-control form-control-sm pb-e" aria-label="${esc(v.label)} 종료일">`}
                <button type="button" class="btn btn-link btn-sm pb-x" title="빼기" aria-label="빼기"><i class="fas fa-times"></i></button>
            </div>`);
            // 단계 순서대로 끼운다 — 같은 단계 뒤에
            const after = $box.find('.pb-row').filter((_, r) => order.indexOf(r.dataset.ph) <= order.indexOf(k)).last();
            after.length ? $r.insertAfter(after) : $box.find('.pb-list').prepend($r);
            fill();
            $r.find('.pb-n').trigger('focus');
        });
        $box.on('click', '.pb-x', (e) => { $(e.currentTarget).closest('.pb-row').remove(); fill(); });
        $box.on('input', 'input[type=date]', (e) => $(e.target).data('touched', 1));
        $('#pStart, #pDue').on('change', fill);
    }

    /** 고른 기본 작업 — 날짜가 비었거나 거꾸로인 줄이 있으면 null (그 칸에 포커스) */
    _collectBaseTasks() {
        const out = [];
        for (const r of $('#mpPjForm .pj-base .pb-row').get()) {
            const $s = $(r).find('.pb-s'), $e = $(r).find('.pb-e');
            const st = $s.val(), en = $e.length ? $e.val() : st;
            if (!st) { $s.trigger('focus'); return null; }
            if (!en || en < st) { $e.trigger('focus'); return null; }
            out.push({ phase: r.dataset.ph, name: $(r).find('.pb-n').val().trim(), start: st, end: en });
        }
        return out;
    }

    /** 합칠 대상 — 상태 무관 전체 원장을 상위별로 묶어 보여준다 (자기 자신 제외) */
    async _fillMergeTargets(row) {
        const $sel = $('#pMrgTo');
        if ($sel.data('filled')) return;
        try {
            const r = await $.post('/adm/ajax/project/list', { status: '' });
            let html = '<option value="">합칠 대상 프로젝트 선택</option>', open = false;
            (r.data || []).forEach(p => {
                if (p._is_group) {
                    html += `${open ? '</optgroup>' : ''}<optgroup label="${esc(p.pj_name)}">`;
                    open = true;
                } else if (p.num !== row.num) {
                    html += `<option value="${p.num}">${esc(p.pj_name)}${p.pj_status === '보류' ? ' (보류)' : p._is_done ? ' (완료)' : ''}</option>`;
                }
            });
            $sel.html(html + (open ? '</optgroup>' : '')).data('filled', 1);
        } catch (e) {
            $sel.html('<option value="">불러오지 못했습니다</option>');
        }
    }

    async _mergeProject(row, sheet) {
        const into = +$('#pMrgTo').val();
        if (!into) return;
        const toName = $('#pMrgTo option:selected').text();
        const $err = $('#pErr').text('');
        try {
            const pre = await $.post('/adm/ajax/project/merge', { from: row.num, into, dry: 1 });
            if (!pre.result) return $err.text(pre.message || '합치지 못했습니다.');
            const m = pre.moved;
            const ok = await $j.Ui.Dialog.confirm(`"${row.pj_name}" 를 "${toName}" 에 합칠까요?`, {
                desc: `일정 ${m.tasks}건 · 주간보고 기록 ${m.items}건 · 커밋 ${m.commits}건이 옮겨가고 "${row.pj_name}" 는 없어집니다.`
                    + (m.folded > 0 ? ` 같은 단계로 겹치는 일정 ${m.folded}건은 한 줄로 접히고, 옛 일정은 작업 메모에 남습니다.` : '')
                    + ' 되돌릴 수 없습니다.',
            });
            if (!ok) return;
            const r = await $.post('/adm/ajax/project/merge', { from: row.num, into });
            if (!r.result) return $err.text(r.message || '합치지 못했습니다.');
            sheet.close();
            $j.Ui.Toast.show('프로젝트를 합쳤습니다');
            this._load();
        } catch (e) {
            $err.text('합치지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
    }

    async _loadProjectWeekly(num) {
        try {
            const r = await $.post('/adm/ajax/project/weekly-items', { num });
            let h;
            if (!r.result) h = `<div class="hint">${esc(r.message || '불러오지 못했습니다.')}</div>`;
            else if (!r.report) h = '<div class="hint">아직 작성된 주차가 없습니다.</div>';
            else if (!(r.items || []).length) h = `<div class="hint">${r.report.wr_start} ~ ${r.report.wr_end} 주차에 이 프로젝트로 기록된 작업이 없습니다.</div>`;
            else h = `<div class="hint mb-1">${r.report.wr_start} ~ ${r.report.wr_end} · ${esc(r.report.wr_status)}</div>`
                + r.items.map(it => `<div class="pj-wk">${this._ob(it.manage_name || '미배정')} <b>${esc(it.wi_title)}</b>${it.wi_body ? `<div class="body">${esc(it.wi_body)}</div>` : ''}</div>`).join('');
            $('#pWk').html(h);
        } catch (e) {
            $('#pWk').html('<div class="hint">불러오지 못했습니다.</div>');
        }
    }

    /**
     * 상위·프로젝트 이름 AI 검토 (ProjectNameReviewer) — 저장 전 조언이다. 막지 않는다.
     * 걸리는 게 없거나 AI 가 안 되면 그 이름 그대로, 제안이 있으면 고르게 한다.
     * @return {Promise<string|null>} 저장할 이름 · null = [고치기] (저장하지 않고 입력칸으로)
     */
    async _reviewName(kind, name, { group = '', num = 0 } = {}) {
        let r;
        try { r = await $.post('/adm/ajax/project/name-review', { kind, name, group, num }); } catch (e) { return name; }
        if (!r || r.verdict !== 'suggest') return name;
        const what = kind === 'group' ? '상위' : '프로젝트';
        let pick = null;
        const choices = [];
        if (r.suggestion) choices.push({ label: `「${r.suggestion}」로 저장`, tag: 'AI 제안', tone: 'brand', onPick: () => { pick = r.suggestion; } });
        choices.push({ label: '그대로 저장', tag: name, onPick: () => { pick = name; } });
        await $j.Ui.Dialog.open({
            tone: 'info', align: 'left',
            title: `${what} 이름을 한 번 볼까요?`,
            desc: `「${name}」`,
            notes: [...(r.issues || []), ...(r.similar || []).map(x => ({ k: '같은 일로 보임', v: x.name }))],
            hint: 'AI 검토는 조언입니다. 규칙: 상위 = 제품·영역, 프로젝트 = 결과물 하나, 단계(기획·개발·오픈)는 작업으로.',
            choices, cancel: '고치기',
        });
        return pick;
    }

    /**
     * 상위 고르기 칸 — 브라우저 기본 목록(datalist)은 모양을 못 맞춰서 직접 그린다.
     * 칸에 보이는 글자가 곧 값이다(숨은 번호 없음 — 저장 때 _resolveGroup 이 이름으로 찾고, 없으면 만든다).
     * 목록은 this.groups 를 그때그때 읽으니 시트 안에서 새로 생긴 상위도 바로 보인다.
     */
    _groupCombo(sel, { width } = {}) {
        const $in = $(sel);
        const $wrap = $('<div class="mp-combo"></div>').css(width ? { flex: `0 0 ${width}` } : {});
        $in.wrap($wrap);
        const $list = $('<div class="mp-combo-list" role="listbox"></div>').hide().insertAfter($in);
        const norm = v => String(v || '').replace(/\s/g, '').toLowerCase();
        const cnt = {};
        this.projects.forEach(p => { if (p.group_num) cnt[p.group_num] = (cnt[p.group_num] || 0) + 1; });
        let idx = -1;
        const items = () => $list.find('[data-v]');
        const render = () => {
            const q = norm($in.val());
            const hit = this.groups.filter(g => !q || norm(g.pg_name).includes(q)).sort((a, b) => a.pg_name.localeCompare(b.pg_name, 'ko'));
            const exact = hit.some(g => norm(g.pg_name) === q);
            let h = q ? '' : '<div class="it none" data-v="">없음 (미분류)</div>';
            h += hit.map(g => `<div class="it ${norm(g.pg_name) === q ? 'on' : ''}" data-v="${esc(g.pg_name)}"><span>${esc(g.pg_name)}</span>${cnt[g.num] ? `<span class="n">${cnt[g.num]}</span>` : ''}</div>`).join('');
            if (q && !exact) h += `<div class="new"><i class="fas fa-plus"></i> 「${esc($in.val().trim())}」 새 상위로 만듭니다 (저장할 때)</div>`;
            $list.html(h).show();
            idx = -1;
        };
        const pick = v => { $in.val(v); $list.hide(); };
        const move = d => {
            const $it = items();
            if (!$it.length) return;
            idx = (idx + d + $it.length) % $it.length;
            $it.removeClass('cur').eq(idx).addClass('cur')[0].scrollIntoView({ block: 'nearest' });
        };
        $in.on('focus input', render)
            .on('blur', () => setTimeout(() => $list.hide(), 120))
            .on('keydown', (e) => {
                if (!$list.is(':visible')) return;
                if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
                else if (e.key === 'Escape') { e.stopPropagation(); $list.hide(); }
                else if (e.key === 'Enter' && idx >= 0) { e.preventDefault(); e.stopImmediatePropagation(); pick(items().eq(idx).attr('data-v')); }
            });
        // mousedown — blur 보다 먼저 와야 목록이 닫히기 전에 고른다
        $list.on('mousedown', '[data-v]', (e) => { e.preventDefault(); pick(e.currentTarget.dataset.v); });
    }

    /**
     * 작업 시트의 프로젝트 고르기 — 상위 칸처럼 쳐서 찾는다. 값은 그대로 #tProject(select)가 들고,
     * 검색칸은 늘 그 선택의 이름을 보여준다: 고르지 않고 나가면 선택된 이름으로 되돌리고, 비우고 나가면 선택을 푼다.
     * (검색칸 글자와 실제 값이 어긋난 채 저장되지 않게 — CLAUDE.md 「검색 인풋에 숨은 상태를 두지 않는다」)
     * 상위 이름으로도 찾는다 — 「결제」를 치면 결제·정산 아래 프로젝트가 다 나온다.
     */
    _projectCombo() {
        const $sel = $('#tProject').hide();
        const $in = $('<input class="form-control form-control-sm" id="tProjectQ" placeholder="프로젝트 검색 (이름·상위)" aria-label="프로젝트" autocomplete="off">');
        const $wrap = $('<div class="mp-combo" style="flex:1 1 auto;min-width:0"></div>').insertBefore($sel).append($in, $sel);
        const $list = $('<div class="mp-combo-list" role="listbox"></div>').hide().appendTo($wrap);
        const norm = v => String(v || '').replace(/\s/g, '').toLowerCase();
        const label = () => { const n = +$sel.val(); const p = this.projects.find(x => x.num === n); return p ? p.label : ''; };
        const sync = () => $in.val(label());
        let idx = -1;
        const items = () => $list.find('[data-v]');
        const render = (all) => {
            const q = all ? '' : norm($in.val());
            const cur = +$sel.val();
            const hit = this.projects.filter(p => !q || norm(p.name).includes(q) || norm(p.group).includes(q));
            let h = '', last = null;
            hit.forEach(p => {
                if (p.group !== last) { h += `<div class="grp">${esc(p.group || '상위 없음')}</div>`; last = p.group; }
                h += `<div class="it ${p.num === cur ? 'on' : ''}" data-v="${p.num}"><span>${esc(p.name)}</span></div>`;
            });
            if (!hit.length) h = '<div class="new">찾는 프로젝트가 없습니다 — 옆의 [추가]로 등록하세요</div>';
            $list.html(h).show();
            idx = hit.length === 1 ? 0 : -1;
            if (idx === 0) items().eq(0).addClass('cur');
        };
        const pick = v => { $sel.val(v).trigger('change'); $list.hide(); };
        const move = d => {
            const $it = items();
            if (!$it.length) return;
            idx = (idx + d + $it.length) % $it.length;
            $it.removeClass('cur').eq(idx).addClass('cur')[0].scrollIntoView({ block: 'nearest' });
        };
        $sel.on('change', sync);
        // 목록은 누르거나(↓) 칠 때 연다 — 시트가 열리며 칸에 포커스가 가도 목록이 덮지 않게.
        // 처음 열 때는 고른 것이 있어도 전체를 보여준다 (고른 이름으로 걸러 한 줄만 남으면 바꾸기 어렵다)
        $in.on('focus', () => $in.trigger('select'))
            .on('click', () => { if (!$list.is(':visible')) render(true); })
            .on('input', () => render(false))
            .on('blur', () => setTimeout(() => {
                $list.hide();
                if ($in.val().trim() === '' && $sel.val()) $sel.val('').trigger('change');
                else sync();
            }, 120))
            .on('keydown', (e) => {
                if (!$list.is(':visible')) { if (e.key === 'ArrowDown') { e.preventDefault(); render(true); } return; }
                if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
                else if (e.key === 'Escape') { e.stopPropagation(); $list.hide(); }
                else if (e.key === 'Enter') { e.preventDefault(); if (idx >= 0) pick(items().eq(idx).attr('data-v')); }
            });
        $list.on('mousedown', '[data-v]', (e) => { e.preventDefault(); pick(e.currentTarget.dataset.v); $in.trigger('blur'); });
        $list.on('mousedown', '.grp, .new', (e) => e.preventDefault());
        sync();
    }

    /** 버튼을 잠그고 '검토 중…' 으로 둔 채 fn 을 기다린다 */
    async _busy(btn, fn) {
        const $b = $(btn), txt = $b.html();
        $b.prop('disabled', true).text('검토 중…');
        try { return await fn(); } finally { $b.prop('disabled', false).html(txt); }
    }

    /** 상위 — 있는 이름이면 그것(띄어쓰기 무시: 줄기가 둘로 갈리면 색도 갈린다), 없는 이름이면 이름 검토 뒤 먼저 만든다. 비우면 null · [고치기]면 false */
    async _resolveGroup(gName) {
        gName = String(gName || '').trim();
        if (!gName) return null;
        const g = this.groups.find(x => x.pg_name.replace(/\s/g, '') === gName.replace(/\s/g, ''));
        if (g) return g;
        const reviewed = await this._reviewName('group', gName);
        if (reviewed === null) return false;
        if (reviewed !== gName) return this._resolveGroup(reviewed);   // 제안 이름이 이미 있는 상위일 수 있다
        const rg = await $.post('/adm/ajax/project/group/save', { pg_name: gName });
        if (!rg.result) throw new Error(rg.message || '상위 프로젝트를 만들지 못했습니다.');
        const ng = { num: +rg.num, pg_name: gName };
        this.groups.push(ng);
        return ng;
    }

    async _saveProject(row, sheet) {
        const $err = $('#pErr').text('');
        const name = $('#pName').val().trim();
        if (!name) { $('#pName').trigger('focus'); return $err.text('프로젝트명을 입력하세요.'); }
        const tasks = row.num ? [] : this._collectBaseTasks();
        if (tasks === null) return $err.text('기본 작업의 날짜를 확인하세요 (비었거나 종료일이 시작일보다 빠릅니다).');
        const owners = $('#mpPjForm [name=pOwners]:checked').map((_, i) => i.value).get();
        if (tasks.length && !owners.length) return $err.text('기본 작업을 넣으려면 담당자를 한 명 이상 고르세요.');
        const $btn = $('#mpPjForm button[type=submit]').prop('disabled', true).text('AI 검토 중…');
        const stop = () => $btn.prop('disabled', false).text('저장');
        try {
            // 새 이름이거나 이름을 바꿨을 때만 검토한다
            const pjName = row.num && name === row.pj_name ? name : await this._reviewName('project', name, { group: $('#pGroup').val().trim(), num: row.num || 0 });
            if (pjName === null) { stop(); return $('#pName').trigger('focus'); }
            $('#pName').val(pjName);
            const g = await this._resolveGroup($('#pGroup').val());
            if (g === false) { stop(); return $('#pGroup').trigger('focus'); }
            $btn.text('저장 중…');
            const r = await $.post('/adm/ajax/project/save', {
                num: row.num || '', pj_name: pjName, pj_group: g ? g.num : '',
                pj_status: $('#pStatus').val(), pj_progress: $('#pProg').val(), pj_completed: $('#pDone').val(),
                pj_started: $('#pStart').val(), pj_due: $('#pDue').val(), pj_memo: $('#pMemo').val(),
                pj_cal_hidden: $('#pHidden').prop('checked') ? 1 : 0,
                owner_nums: owners, tasks,
            });
            if (!r.result) { $btn.prop('disabled', false).text('저장'); return $err.text(r.message || '저장하지 못했습니다.'); }
            sheet.close();
            $j.Ui.Toast.show(row.num ? '저장했습니다' : tasks.length ? `프로젝트와 작업 ${tasks.length}건을 등록했습니다` : '프로젝트를 등록했습니다');
            this._load();
        } catch (e) {
            $btn.prop('disabled', false).text('저장');
            $err.text(e.message || '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
    }

    /* ── 상위 프로젝트 관리 ─────────────────────────── */

    async _openGroups() {
        let list = [];
        try {
            const r = await $.post('/adm/ajax/project/groups');
            if (!r.result) return Util.swalert('목록을 불러오지 못했습니다.');
            list = r.data || [];
        } catch (e) {
            return Util.swalert('목록을 불러오지 못했습니다.');
        }
        const html = `<div class="mp-groups">
            <div class="hint mb-2">상위 ${list.length}개 · 프로젝트를 누르면 프로젝트 시트가 열립니다. 상위를 지우면 소속 프로젝트는 '미분류'가 됩니다.</div>
            <div class="g-row"><input class="form-control form-control-sm" id="gNew" placeholder="새 상위 프로젝트명"><button type="button" class="btn btn-primary btn-sm text-nowrap" data-g-act="add">추가</button></div>
            ${list.map(g => `<div class="g-item" data-g="${g.num}" data-cnt="${g.child_count}">
                <div class="g-row">
                    <input class="form-control form-control-sm" value="${esc(g.pg_name)}" data-orig="${esc(g.pg_name)}" aria-label="상위 프로젝트명">
                    <span class="hint text-nowrap">프로젝트 ${g.child_count}</span>
                    <button type="button" class="btn btn-default btn-sm" data-g-act="save">저장</button>
                    <button type="button" class="btn btn-outline-danger btn-sm" data-g-act="del" aria-label="삭제"><i class="fas fa-trash"></i></button></div>
                <div class="g-kids">${g.children.length ? g.children.map(c => `<button type="button" class="g-kid${c.pj_progress >= 100 ? ' done' : c.pj_status === '보류' ? ' hold' : ''}" data-pj="${c.num}">${esc(c.pj_name)}<span>${c.pj_progress >= 100 ? '완료' : c.pj_status === '보류' ? '보류' : c.pj_progress + '%'}</span></button>`).join('') : '<span class="hint">소속 프로젝트 없음</span>'}</div>
            </div>`).join('')}
        </div>`;
        const m = $j.Ui.Modal.open({ id: 'mpGroups', title: '상위 프로젝트 목록', html, width: '640px', maxWidth: 680 });
        m.el.on('click', '[data-pj]', (e) => { m.close(); this._openProjectSheet(+e.currentTarget.dataset.pj); });
        const done = (msg) => { $j.Ui.Toast.show(msg); this._load(); this._openGroups(); };
        m.el.on('click', '[data-g-act]', async (e) => {
            const act = e.currentTarget.dataset.gAct;
            const $row = $(e.currentTarget).closest('.g-item, .g-row');
            try {
                if (act === 'add') {
                    const typed = $('#gNew').val().trim();
                    if (!typed) return $('#gNew').trigger('focus');
                    const name = await this._busy(e.currentTarget, () => this._reviewName('group', typed));
                    if (name === null) return $('#gNew').trigger('focus');
                    const r = await $.post('/adm/ajax/project/group/save', { pg_name: name });
                    if (!r.result) return Util.swalert(r.message || '추가하지 못했습니다.');
                    return done('추가했습니다');
                }
                const num = +$row.data('g');
                if (act === 'save') {
                    const typed = $row.find('input').first().val().trim();
                    if (!typed || typed === $row.find('input').first().data('orig')) return;
                    const name = await this._busy(e.currentTarget, () => this._reviewName('group', typed, { num }));
                    if (name === null) return $row.find('input').first().trigger('focus');
                    const r = await $.post('/adm/ajax/project/group/save', { num, pg_name: name });
                    if (!r.result) return Util.swalert(r.message || '저장하지 못했습니다.');
                    return done('저장했습니다');
                }
                if (act === 'del') {
                    const cnt = +$row.data('cnt');
                    const ok = await $j.Ui.Dialog.confirm(`"${$row.find('input').first().data('orig')}" 상위 프로젝트를 삭제할까요?`, cnt ? { desc: `소속 하위 ${cnt}개는 지우지 않고 '미분류'로 바뀝니다.` } : {});
                    if (!ok) return;
                    const r = await $.post('/adm/ajax/project/group/delete', { num });
                    if (!r.result) return Util.swalert(r.message || '삭제하지 못했습니다.');
                    return done('삭제했습니다');
                }
            } catch (err) {
                Util.swalert('처리하지 못했습니다. 잠시 후 다시 시도해 주세요.');
            }
        });
    }

    _taskOptions(sel) {
        return this.tasks.slice().sort((a, b) => (a.mt_cur_start < b.mt_cur_start ? -1 : 1)).map(t =>
            `<option value="${t.num}" ${t.num === +sel ? 'selected' : ''}>[${esc(this._phaseLbl(t))}] ${esc(t.mt_title)} · ${esc(this._ownerTxt(t))}</option>`).join('');
    }

    // stamp — 날짜 머리글이 없는 곳(작업수정 모달)에서 언제 남긴 기록인지 시각까지 붙인다
    _logItem(l, stamp = false) {
        const t = this._byId(l.mt_num);
        let extra = '';
        if (l.ml_from_end && l.ml_to_end) {
            const d = this._bdiff(l.ml_from_end, l.ml_to_end);
            const span = (st, en) => (!st || st === en ? fmt(en) : `${fmt(st)} ~ ${fmt(en)}`);
            extra = `<div class="hint">${span(l.ml_from_start, l.ml_from_end)} → <b>${span(l.ml_to_start, l.ml_to_end)}</b>${d ? ` (${d > 0 ? '+' : ''}${d}일)` : ''}</div>`;
        }
        const at = !stamp ? '' : l.created_at ? `${fmt(l.created_at.slice(0, 10))} ${l.created_at.slice(11, 16)}` : fmt(l.ml_date);
        const who = [at, l.manage_name ? esc(l.manage_name) : ''].filter(Boolean).join(' · ');
        return `<div class="mp-log"><span class="ty ty-${esc(l.ml_type)}">${LOG_TYPE[l.ml_type] || esc(l.ml_type)}</span><div>
            <div><b>${esc(t ? t.mt_title : (l.mt_title || '삭제된 작업'))}</b>${l.ml_status && l.ml_type === 'daily' ? ` <span class="stt stt-${esc(l.ml_status)}">${esc(l.ml_status)}</span>` : ''}${l.ml_progress != null && l.ml_type === 'daily' ? ` <span class="hint">${l.ml_progress}%</span>` : ''}</div>
            ${l.ml_text ? `<div class="txt">${esc(l.ml_text)}</div>` : ''}${l.ml_issue ? `<div class="issue txt">이슈: ${esc(l.ml_issue)}</div>` : ''}${extra}
            ${who ? `<div class="who">${who}</div>` : ''}</div></div>`;
    }

    _renderFeed() {
        const [ws, we] = this._reportWeek(this.today);
        const logs = this.logs.filter(l => this.feedAll || (l.ml_date >= ws && l.ml_date <= we));
        const groups = {};
        logs.forEach(l => (groups[l.ml_date] = groups[l.ml_date] || []).push(l));
        const dates = Object.keys(groups).sort().reverse();
        return `<div class="d-flex align-items-center mb-1" style="gap:8px">
                <b>${this.feedAll ? '전체 기록' : `이번 주 기록 · ${fmt(ws)} ~ ${fmt(we)}`}</b>
                <button type="button" class="btn btn-link btn-sm ml-auto" data-act="feedAll">${this.feedAll ? '이번 주만 보기' : '전체 기록 보기'}</button>
            </div>
            ${dates.length ? dates.map(d => `<div class="day-h">${fmt(d)}${d === this.today ? ' · 오늘' : ''}</div>${groups[d].map(l => this._logItem(l)).join('')}`).join('')
                : '<div class="mp-empty">아직 기록이 없습니다. 왼쪽에서 오늘 진행 내용을 입력하면 여기에 쌓입니다.</div>'}`;
    }

    _renderDaily() {
        const st = this.statuses.map(s => `<option>${s}</option>`).join('');
        return `<div class="daily p-3">
            <form id="mpDaily" novalidate class="mp-sheet">
                <h6 style="font-weight:700">오늘 진행 내용 입력</h6>
                <div class="hint mb-2">저장하면 작업의 상태·진행률이 바뀌고, 그 주 주간보고에 모입니다.</div>
                <div class="form-row">
                    <div class="form-group col-6"><label for="dDate">날짜</label><input class="form-control form-control-sm" type="date" id="dDate" value="${this.today}"></div>
                    <div class="form-group col-6"><label for="dStatus">상태</label><select class="form-control form-control-sm" id="dStatus">${st}</select></div>
                </div>
                <div class="form-group"><label for="dTask">작업 <span class="req">*</span></label><select class="form-control form-control-sm" id="dTask"><option value="">작업을 고르세요</option>${this._taskOptions()}</select></div>
                <div class="form-group"><label for="dProg">진행률 <span id="dProgV">0%</span></label><input class="custom-range" type="range" id="dProg" min="0" max="100" step="5" value="0"></div>
                <div class="form-group"><label for="dText">오늘 한 일 <span class="req">*</span></label><textarea class="form-control form-control-sm" id="dText" rows="3" placeholder="예) 발주 상세 화면 다크모드 시안 완료"></textarea></div>
                <div class="form-group"><label for="dIssue">이슈·막힌 점</label><textarea class="form-control form-control-sm" id="dIssue" rows="2" placeholder="없으면 비워두세요"></textarea></div>
                <label class="mp-chk mb-2"><input type="checkbox" id="dRes"> 이 작업 일정이 바뀌었어요</label>
                <div class="box" id="dResBox" style="display:none">
                    <div class="form-row">
                        <div class="form-group col-6"><label for="dNewStart">변경 시작일</label><input class="form-control form-control-sm" type="date" id="dNewStart"></div>
                        <div class="form-group col-6" id="dNewEndWrap"><label for="dNewEnd">변경 종료일</label><input class="form-control form-control-sm" type="date" id="dNewEnd"></div>
                    </div>
                    <div class="form-group mb-1"><label for="dReason">변경 사유 <span class="req">*</span></label><input class="form-control form-control-sm" id="dReason" placeholder="예) 결제사 API 응답 지연"></div>
                    <div class="hint" id="dResHint"></div>
                </div>
                <div class="err" id="dErr" role="alert"></div>
                <button class="btn btn-primary btn-block" type="submit">저장</button>
            </form>
            <section id="mpFeed">${this._renderFeed()}</section>
        </div>`;
    }

    /* ── 이벤트 ─────────────────────────────────────── */

    _bind() {
        $('#mpViews').on('click', '[data-v]', (e) => {
            this.view = e.currentTarget.dataset.v;
            try { localStorage.setItem('mp.view', this.view); } catch (_) { /* 무시 */ }
            this._render();
        });
        $('#mpAdd').on('click', () => this._openSheet(null));
        $('#mpPjAdd').on('click', () => this._openProjectSheet(0));
        $('#mpFHidden').on('change', (e) => { this.f.showHidden = e.target.checked; this._render(); });
        $('#mpFDone').on('change', (e) => {
            this.f.hideDone = e.target.checked;
            try { localStorage.setItem('mp.hideDone', this.f.hideDone ? '1' : '0'); } catch (_) { /* 무시 */ }
            this._render();
        });
        let kwTimer = null;
        $('#mpView').on('change', '#pjFStatus', (e) => { this.pf.status = e.target.value; this._loadProjects(); });
        $('#mpView').on('change', '#pjFEmpty', (e) => { this.pf.showEmpty = e.target.checked; $('#pjTable').html(this._renderProjects()); });
        $('#mpView').on('input', '#pjFKeyword', (e) => { clearTimeout(kwTimer); kwTimer = setTimeout(() => { this.pf.keyword = e.target.value.trim(); this._loadProjects(); }, 250); });
        $('#mpView').on('click', '[data-act="pjGroups"]', () => this._openGroups());
        $('#mpView').on('click', 'tr[data-pj]', (e) => {
            const $a = $(e.target).closest('[data-act]');
            if ($a.data('act') === 'pjHide') return this._toggleHidden(+$a.data('pj'), !+$a.data('hidden'));
            if ($a.data('act') === 'pjAddTask') {
                const pr = this.pjRows.find(x => +x.num === +$a.data('pj') && !x._is_group);
                if (pr) this._ensureProjectOption(pr);
                return this._openSheet(null, null, +$a.data('pj'));
            }
            const $t = $(e.target).closest('[data-task]');   // 일정 줄은 그 작업 시트로 — 나머지 칸은 프로젝트 시트
            if ($t.length) return this._openSheet(+$t.data('task'));
            this._openProjectSheet(+e.currentTarget.dataset.pj);
        });
        $('#mpFOwner').on('change', (e) => { this.f.owner = +e.target.value || 0; this._render(); });
        $('#mpFPhase').on('change', (e) => { this.f.phase = e.target.value; this._render(); });
        $('#mpFProject').on('change', (e) => { this.f.project = +e.target.value || 0; this._render(); });
        $('#mpFLate').on('change', (e) => { this.f.late = e.target.checked; this._render(); });
        $('#mpFRange').on('change', (e) => { this.f.range = e.target.value; this._render(); });

        $('#mpView').on('click', 'tr[data-id]', (e) => this._openSheet(+e.currentTarget.dataset.id));
        $('#mpView').on('click', '[data-act="feedAll"]', () => { this.feedAll = !this.feedAll; $('#mpFeed').html(this._renderFeed()); });
    }

    _bindDaily() {
        const fill = () => {
            const t = this._byId($('#dTask').val());
            if (!t) return;
            $('#dStatus').val(t.mt_status || '대기');
            $('#dProg').val(t.mt_progress || 0);
            $('#dProgV').text((t.mt_progress || 0) + '%');
            $('#dNewStart').val(t.mt_cur_start);
            $('#dNewEnd').val(t.mt_cur_end);
            $('#dNewEndWrap').toggle(!t.is_ms);
            hint();
        };
        const hint = () => {
            const t = this._byId($('#dTask').val());
            if (!t) return;
            const ne = t.is_ms ? $('#dNewStart').val() : $('#dNewEnd').val();
            const dd = this._bdiff(t.mt_plan_end, ne);
            $('#dResHint').text(`기준 종료일 ${fmt(t.mt_plan_end)} · 변경 시 ${dd > 0 ? '+' + dd + '일 지연' : dd < 0 ? (-dd) + '일 단축' : '기준과 동일'}`);
        };
        $('#dTask').on('change', fill);
        $('#dNewStart, #dNewEnd').on('input', hint);
        $('#dProg').on('input', (e) => $('#dProgV').text(e.target.value + '%'));
        this._linkDone('#dStatus', '#dProg', '#dProgV');
        $('#dRes').on('change', (e) => {
            $('#dResBox').toggle(e.target.checked);
            if (e.target.checked && !$('#dTask').val()) $('#dErr').text('먼저 작업을 고르세요.');
        });
        $('#mpDaily').on('submit', (e) => { e.preventDefault(); this._saveDaily(); });
    }

    async _saveDaily() {
        const $err = $('#dErr').text('');
        const id = $('#dTask').val(), text = $('#dText').val().trim();
        if (!id) return $err.text('작업을 고르세요.');
        if (!text) { $('#dText').trigger('focus'); return $err.text('오늘 한 일을 입력하세요.'); }
        const t = this._byId(id);
        const res = $('#dRes').prop('checked');
        if (res) {
            const ns = $('#dNewStart').val(), ne = t.is_ms ? ns : $('#dNewEnd').val();
            if (!ns || !ne) return $err.text('변경 날짜를 입력하세요.');
            if (ne < ns) return $err.text('종료일이 시작일보다 빠릅니다.');
            if (!$('#dReason').val().trim()) { $('#dReason').trigger('focus'); return $err.text('변경 사유를 입력하세요.'); }
        }

        const $btn = $('#mpDaily button[type=submit]').prop('disabled', true).text('저장 중…');
        try {
            const r = await $.post('/adm/ajax/project/master/daily', {
                mt_num: id, ml_date: $('#dDate').val(), ml_status: $('#dStatus').val(), ml_progress: $('#dProg').val(),
                ml_text: text, ml_issue: $('#dIssue').val().trim(),
                reschedule: res ? 1 : '', new_start: $('#dNewStart').val(), new_end: $('#dNewEnd').val(), reason: $('#dReason').val().trim(),
            });
            if (!r.result) { $btn.prop('disabled', false).text('저장'); return $err.text(r.message || '저장하지 못했습니다.'); }
            $j.Ui.Toast.show('오늘 진행 내용을 저장했습니다');
            await this._load();   // 폼까지 새로 그려 입력칸이 비워진다
        } catch (e) {
            $btn.prop('disabled', false).text('저장');
            $err.text('저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
    }

    /* ── 작업 시트 ──────────────────────────────────── */

    /**
     * 작업 시트 프로젝트 후보(projectOptions)는 끝난 프로젝트를 뺀다 — 그 프로젝트 화면에서 [일정 추가]를 눌렀으면
     * 끝났어도 후보에 넣어야 미리 골라진다. 원장 한 줄(num·pj_name·pj_group)로 후보 한 줄을 만든다
     */
    _ensureProjectOption(r) {
        const num = +r.num;
        if (!num || this.projects.some(p => p.num === num)) return;
        const g = this.groups.find(x => x.num === +r.pj_group);
        const group = g ? g.pg_name : '';
        this.projects.push({ num, name: r.pj_name, group, group_num: g ? g.num : null, label: (group ? group + ' › ' : '') + r.pj_name });
        this.projects.sort((a, b) => (!a.group - !b.group) || a.group.localeCompare(b.group, 'ko') || a.name.localeCompare(b.name, 'ko'));
    }

    /**
     * 상태 ↔ 진행률 맞물리기 — 100% 면 완료, 완료면 100%, 완료인데 100 아래로 내리면 진행중.
     * 기한 초과는 상태를 보므로, 100% 인데 '대기'로 남으면 끝낸 일이 기한 초과로 뜬다 (서버 MasterPlanService::alignDone 도 같은 셈)
     */
    _linkDone(stSel, pgSel, pvSel) {
        $(pgSel).on('input', (e) => {
            const v = +e.target.value, st = $(stSel).val();
            if (v >= 100 && st !== '보류') $(stSel).val('완료');
            else if (v < 100 && st === '완료') $(stSel).val('진행중');
        });
        $(stSel).on('change', (e) => { if (e.target.value === '완료') { $(pgSel).val(100); $(pvSel).text('100%'); } });
    }

    /** @param pjNum 새 작업일 때 미리 고를 프로젝트 (프로젝트 보기·시트의 [일정 추가]) */
    _openSheet(id, date, pjNum = 0) {
        const t = id ? this._byId(id) : null;
        const phs = t ? t.phases : ['plan'];
        const d = date || this.today;
        const logs = t ? this.logs.filter(l => l.mt_num === t.num) : [];
        const others = this.tasks.filter(x => !t || x.num !== t.num);
        const ownerNums = t ? t.owners.map(o => o.num) : [];
        // 원장에서 빠진 담당자(퇴사·부서이동)도 이 작업에 걸려 있으면 보여준다
        const cands = [...this.members.map(m => ({ num: +m.num, name: m.manage_name }))];
        (t ? t.owners : []).forEach(o => { if (!cands.some(c => c.num === o.num)) cands.push(o); });

        const dateRow = (label, idS, idE, vs, ve) => `<div class="form-row rng">
            <div class="form-group col-6 mb-1"><label for="${idS}" class="lbS">${label}시작</label><input class="form-control form-control-sm" type="date" id="${idS}" value="${vs}"></div>
            <div class="form-group col-6 mb-1 fe"><label for="${idE}">${label}종료</label><input class="form-control form-control-sm" type="date" id="${idE}" value="${ve}"></div></div>`;

        const html = `<form id="mpTaskForm" class="mp-sheet" novalidate>
            <div class="form-group"><label for="tProject">프로젝트 <span class="req">*</span> <span class="hint">같은 프로젝트의 기획·개발·테스트·오픈은 캘린더에서 한 색으로 묶입니다</span></label>
                <div class="d-flex" style="gap:6px">
                    <select class="form-control form-control-sm" id="tProject"><option value="">프로젝트를 고르세요</option>${this.projects.map(p => `<option value="${p.num}" ${(t ? t.pj_num : (pjNum || (this.f.project > 0 ? this.f.project : 0))) === p.num ? 'selected' : ''}>${esc(p.label)}</option>`).join('')}</select>
                    <button type="button" class="btn btn-default btn-sm text-nowrap" id="tPjEditBtn" title="고른 프로젝트의 이름·상위 고치기"><i class="fas fa-pen"></i> 수정</button>
                    <button type="button" class="btn btn-default btn-sm text-nowrap" id="tPjAddBtn"><i class="fas fa-plus"></i> 추가</button>
                </div>
                <div class="box mt-2 mb-0" id="tPjAdd" style="display:none">
                    <div class="bt"><span id="tPjBoxTitle">새 프로젝트</span> <span class="hint" style="font-weight:400">상위는 비워도 되고, 없는 이름이면 함께 만듭니다 · 메모·진행률은 「프로젝트」 보기에서</span></div>
                    <div class="d-flex" style="gap:6px">
                        <input class="form-control form-control-sm" id="tPjGroup" placeholder="상위 (고르거나 입력)" aria-label="상위 프로젝트" autocomplete="off">
                        <input class="form-control form-control-sm" id="tPjName" placeholder="프로젝트명" aria-label="프로젝트명">
                        <button type="button" class="btn btn-primary btn-sm text-nowrap" id="tPjSave">등록</button>
                        <button type="button" class="btn btn-default btn-sm text-nowrap" id="tPjCancel">취소</button>
                    </div>
                    <div class="err mt-1" id="tPjErr" style="min-height:0"></div>
                </div></div>
            <div class="form-group"><label for="tName">작업명 <span class="hint">비워도 됩니다 · 한 프로젝트 안에서 같은 단계를 나눌 때 — 예) 모바일 화면, 관리자, 영상제작</span></label>
                <input class="form-control form-control-sm" id="tName" maxlength="100" value="${esc(t?.mt_name || '')}" placeholder="비우면 프로젝트명만 보입니다"></div>
            <div class="form-group"><label>구분 <span class="req">*</span> <span class="hint">여러 개 선택 가능</span></label>
                <div class="seg">${Object.entries(this.PH).map(([k, v]) => `<label><input type="checkbox" name="tPhases" value="${k}" ${phs.includes(k) ? 'checked' : ''}><span class="ph-tag ph-${k}">${v.label}</span></label>`).join('')}</div>
                <div class="hint mt-1">오픈·기타 일정만 고르면 하루짜리 일정, 그 밖의 단계는 기간 막대로 표시됩니다. 개발마감은 테스트에서 나온 수정을 반영하는 기간, 기타 작업은 영상제작처럼 위 단계에 안 맞는 기간짜리 일입니다.</div></div>
            <div class="form-group"><label>담당자 <span class="req">*</span> <span class="hint">여러 명 선택 가능</span></label>
                <div class="seg">${cands.map(c => `<label><input type="checkbox" name="tOwners" value="${c.num}" ${ownerNums.includes(c.num) ? 'checked' : ''}>${this._ob(c.name)}</label>`).join('')}</div></div>
            ${t ? `<div class="box"><div class="bt">일정 ${this._flags(t)}</div>
                    ${dateRow('', 'tCS', 'tCE', t.mt_cur_start, t.mt_cur_end)}
                    ${t.mt_plan_start !== t.mt_cur_start || t.mt_plan_end !== t.mt_cur_end ? `<div class="hint">원래 일정 ${this._baseRange(t)}</div>` : ''}
                    <div id="tModeWrap" style="display:none" class="mt-2">
                        <div class="seg">
                            <label><input type="radio" name="tMode" value="fix">단순 수정</label>
                            <label><input type="radio" name="tMode" value="delay">일정 지연·변경</label>
                        </div>
                        <div class="hint mt-1" id="tModeHint">일정을 바꿨습니다. 어떤 수정인지 고르세요.</div>
                    </div>
                    <div class="form-group mt-2 mb-0" id="tReasonWrap" style="display:none"><label for="tReason">지연 사유 <span class="req" id="tReasonReq">*</span> <span class="hint">주간업무보고 캘린더 아래에 나옵니다</span></label><input class="form-control form-control-sm" id="tReason" value="${esc(t.mt_delay_reason || '')}" placeholder="예) 결제사 API 응답 지연"></div>
                    </div>`
                : `${dateRow('', 'tCS', 'tCE', d, d)}<div class="hint mb-3">처음 입력한 일정이 기준 일정으로 저장되어, 나중에 바뀌면 얼마나 늦어졌는지 비교됩니다.</div>`}
            <div class="form-row">
                <div class="form-group col-4"><label for="tStatus">상태</label><select class="form-control form-control-sm" id="tStatus">${this.statuses.map(s => `<option ${s === (t?.mt_status || '대기') ? 'selected' : ''}>${s}</option>`).join('')}</select></div>
                <div class="form-group col-3"><label for="tPri">우선순위</label><select class="form-control form-control-sm" id="tPri">${['높음', '보통', '낮음'].map(p => `<option ${p === (t?.mt_priority || '보통') ? 'selected' : ''}>${p}</option>`).join('')}</select></div>
                <div class="form-group col-5"><label for="tProg">진행률 <span id="tProgV">${t?.mt_progress || 0}%</span></label><input class="custom-range" type="range" id="tProg" min="0" max="100" step="5" value="${t?.mt_progress || 0}"></div>
            </div>
            <div class="form-group"><label>선행 작업 <span class="hint">이게 끝나야 시작하는 앞 단계 — 고르면 시작일이 그 다음 날로 맞춰집니다(고칠 수 있음)</span></label>
                <div id="tDepsBox"></div></div>
            <div class="form-group"><label for="tMemo">메모 <span class="hint">관련 화면·기획서 링크 등</span></label><textarea class="form-control form-control-sm" id="tMemo" rows="2">${esc(t?.mt_memo || '')}</textarea></div>
            ${logs.length ? `<details class="form-group mp-hist"><summary>변경 이력 <span class="hint">${logs.length}건</span></summary><div class="mt-2">${logs.map(l => this._logItem(l, true)).join('')}</div></details>` : ''}
            <div class="err" id="tErr" role="alert"></div>
            <div class="foot">
                ${t ? '<button type="button" class="btn btn-outline-danger btn-sm" id="tDel">삭제</button>' : '<span></span>'}
                <div><button type="button" class="btn btn-default btn-sm" id="tClose">닫기</button> <button type="submit" class="btn btn-primary btn-sm">저장</button></div>
            </div>
        </form>`;

        this.sheet = $j.Ui.Modal.open({ id: 'mpSheet', title: t ? '작업 수정' : '작업 추가', html, mode: 'right', width: '540px' });
        const $f = $('#mpTaskForm');

        const selPhases = () => $f.find('[name=tPhases]:checked').map((_, i) => i.value).get();
        const syncMs = () => {
            const ms = this._isMsPhases(selPhases());
            $f.find('.rng .fe').toggle(!ms);
            $f.find('.rng .lbS').text(ms ? '날짜' : '시작');
        };
        // 일정을 바꾸면 '단순 수정'(원래 일정도 같이 바꿈 · 지연 아님)인지 '일정 지연·변경'(원래 일정 유지 · 사유)인지 고르게 한다
        const chk = () => {
            if (!t) return;
            const ms = this._isMsPhases(selPhases());
            const cs = $('#tCS').val(), ce = ms ? cs : $('#tCE').val();
            const moved = cs !== t.mt_cur_start || ce !== t.mt_cur_end;
            const mode = $f.find('[name=tMode]:checked').val();
            $('#tModeWrap').toggle(moved);
            const wasLate = t.mt_plan_end !== t.mt_cur_end;
            // 지연 사유 — 이번에 지연으로 바꾸면 필수, 날짜는 그대로인데 이미 늦어진 작업이면 언제든 고칠 수 있게 보여준다
            $('#tReasonWrap').toggle(moved ? mode === 'delay' : wasLate);
            $('#tReasonReq').toggle(moved);
            const dd = this._bdiff(t.mt_plan_end, ce);
            $('#tModeHint').text(!mode ? '일정을 바꿨습니다. 어떤 수정인지 고르세요.'
                : mode === 'fix' ? `원래 일정도 같이 바뀌어 지연으로 치지 않습니다.${wasLate ? ' 지금 붙어 있는 지연 표시도 없어집니다.' : ''}`
                : `원래 일정(${this._baseRange(t)})은 그대로 두고 ${dd > 0 ? '+' + dd + '일 지연' : dd < 0 ? (-dd) + '일 단축' : '기준과 같음'}으로 남습니다. 아래 지연 사유가 주간보고에 나옵니다.`);
        };
        $f.on('change', '[name=tPhases]', () => { syncMs(); chk(); });

        // 선행 작업 — 같은 프로젝트의 다른 단계만 후보다. 이름이 모두 프로젝트명이라 단계·일정으로 구분해 보여준다
        const renderDeps = () => {
            const pj = +$('#tProject').val() || 0;
            const keep = new Set($f.find('[name=tDeps]').length
                ? $f.find('[name=tDeps]:checked').map((_, i) => +i.value).get()
                : (t?.deps || []));
            const list = pj ? others.filter(x => x.pj_num === pj) : [];
            $('#tDepsBox').html(!pj ? '<div class="hint">프로젝트를 고르면 같은 프로젝트의 다른 단계가 나옵니다.</div>'
                : !list.length ? '<div class="hint">이 프로젝트에 다른 작업이 아직 없습니다.</div>'
                : `<div class="deps">${list.map(x => `<label><input type="checkbox" name="tDeps" value="${x.num}" ${keep.has(x.num) ? 'checked' : ''}><span class="ph-tag ph-${this._primary(x)}">${esc(this._phaseLbl(x))}</span> <span class="hint">${this._range(x)} · ${esc(this._ownerTxt(x))}</span></label>`).join('')}</div>`);
        };
        // 선행을 고르면 시작일을 선행 작업(여럿이면 가장 늦게 끝나는 것) 다음 영업일로 옮긴다. 기간(영업일 수)은 그대로
        const nextBiz = (d) => { let c = addD(d, 1); while (!this._isBiz(c)) c = addD(c, 1); return c; };
        $f.on('change', '[name=tDeps]', (e) => {
            if (!e.target.checked) return;
            const ends = $f.find('[name=tDeps]:checked').map((_, i) => (this._byId(i.value) || {}).mt_cur_end).get().filter(Boolean);
            if (!ends.length) return;
            const ns = nextBiz(ends.sort().pop());
            const cs = $('#tCS').val(), ce = $('#tCE').val();
            // 기간은 영업일 수로 유지한다 — 달력 날수로 밀면 끝이 주말에 걸린다
            let left = cs && ce ? Math.max(0, this._bdiff(cs, ce)) : 0, ne = ns;
            while (left > 0) { ne = nextBiz(ne); left--; }
            $('#tCS').val(ns);
            $('#tCE').val(ne);
            chk();
            $j.Ui.Toast.show(`선행 작업 다음 날(${fmt(ns)})로 일정을 맞췄습니다`);
        });
        $('#tProject').on('change', renderDeps);
        renderDeps();
        chk();
        $f.on('input change', '#tCS,#tCE,[name=tMode]', chk);
        $('#tProg').on('input', (e) => $('#tProgV').text(e.target.value + '%'));
        this._linkDone('#tStatus', '#tProg', '#tProgV');
        // 같은 한 줄 칸을 '추가'와 '수정'이 같이 쓴다 — 어떤 모드인지는 data-mode 로
        const openPjBox = (mode) => {
            const $box = $('#tPjAdd');
            if ($box.is(':visible') && $box.data('mode') === mode) { $box.hide(); return; }
            const p = this.projects.find(x => String(x.num) === String($('#tProject').val()));
            if (mode === 'edit' && !p) { $j.Ui.Toast.show('먼저 고칠 프로젝트를 고르세요'); return; }
            $box.data('mode', mode).show();
            $('#tPjBoxTitle').text(mode === 'edit' ? '프로젝트 수정' : '새 프로젝트');
            $('#tPjSave').text(mode === 'edit' ? '저장' : '등록');
            $('#tPjGroup').val(mode === 'edit' ? p.group : '');
            $('#tPjName').val(mode === 'edit' ? p.name : '').trigger('focus');
            $('#tPjErr').text('');
        };
        $('#tPjAddBtn').on('click', () => openPjBox('add'));
        $('#tPjEditBtn').on('click', () => openPjBox('edit'));
        $('#tProject').on('change', () => { if ($('#tPjAdd').data('mode') === 'edit') $('#tPjAdd').hide(); });
        $('#tPjCancel').on('click', () => { $('#tPjAdd').hide(); $('#tPjErr').text(''); });
        const savePjBox = () => ($('#tPjAdd').data('mode') === 'edit' ? this._renameProject() : this._addProject(selPhases()));
        $('#tPjSave').on('click', savePjBox);
        // 작업 폼 안이라 Enter 가 작업 저장으로 새지 않게 막는다 (목록이 열려 있으면 콤보가 먼저 고른다)
        this._groupCombo('#tPjGroup', { width: '36%' });
        $('#tPjGroup').on('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); $('#tPjName').trigger('focus'); } });
        $('#tPjName').on('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); savePjBox(); } });
        $('#tClose').on('click', () => this.sheet.close());
        $('#tDel').on('click', (e) => {
            const $b = $(e.currentTarget);
            if (!$b.data('armed')) { $b.data('armed', 1).text('한 번 더 누르면 삭제'); return; }
            this._deleteTask(t);
        });
        $f.on('submit', (e) => { e.preventDefault(); this._saveTask(t, selPhases()); });
        syncMs();
        this._projectCombo();
        setTimeout(() => $('#tProjectQ').trigger(t ? 'blur' : 'focus'), 30);
    }

    /**
     * 시트 안에서 원장에 프로젝트를 바로 등록한다 — 「프로젝트」 보기의 프로젝트 시트와 같은 저장 경로(/adm/ajax/project/save).
     * 상위는 고르거나 새 이름을 적는다 — 새 이름이면 상위(/adm/ajax/project/group/save)부터 만든다.
     * 담당자·시작/예상종료는 지금 시트에 적힌 작업 값으로 채운다(「프로젝트」 보기에서 나중에 고치면 된다).
     * 시트를 닫지 않으려고 전체를 다시 불러오지 않고 목록에만 끼워 넣고 고른다.
     */
    async _addProject(phases) {
        const $err = $('#tPjErr').text('');
        const name = $('#tPjName').val().trim();
        if (!name) { $('#tPjName').trigger('focus'); return $err.text('프로젝트명을 입력하세요.'); }
        const ms = this._isMsPhases(phases);
        const cs = $('#tCS').val(), ce = ms ? cs : $('#tCE').val();
        const $btn = $('#tPjSave').prop('disabled', true);
        try {
            const reviewed = await this._reviewName('project', name, { group: $('#tPjGroup').val().trim() });
            if (reviewed === null) { $btn.prop('disabled', false); return $('#tPjName').trigger('focus'); }
            $('#tPjName').val(reviewed);
            let g;
            try { g = await this._resolveGroup($('#tPjGroup').val()); } catch (ge) { $btn.prop('disabled', false); return $err.text(ge.message); }
            if (g === false) { $btn.prop('disabled', false); return $('#tPjGroup').trigger('focus'); }
            const r = await $.post('/adm/ajax/project/save', {
                pj_name: reviewed, pj_group: g ? g.num : '', pj_status: '진행중', pj_progress: 0,
                pj_started: cs, pj_due: ce,
                owner_nums: $('#mpTaskForm [name=tOwners]:checked').map((_, i) => i.value).get(),
            });
            $btn.prop('disabled', false);
            if (!r.result) return $err.text(r.message || '등록하지 못했습니다.');
            const opt = { num: +r.num, label: (g ? g.pg_name + ' › ' : '') + reviewed };
            this.projects.push(opt);
            $('#tProject').append(`<option value="${opt.num}">${esc(opt.label)}</option>`).val(opt.num).trigger('change');
            $('#tPjAdd').hide(); $('#tPjName, #tPjGroup').val('');
            $j.Ui.Toast.show('프로젝트를 등록했습니다');
        } catch (e) {
            $btn.prop('disabled', false);
            $err.text('등록하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
    }

    /**
     * 고른 프로젝트의 이름·상위를 원장에서 고친다. 작업 이름은 원장 이름을 따라가므로
     * 화면 뒤쪽(캘린더·목록)도 다시 불러 맞춘다 — 시트는 그대로 둔다.
     */
    async _renameProject() {
        const $err = $('#tPjErr').text('');
        const num = $('#tProject').val();
        const name = $('#tPjName').val().trim();
        if (!name) { $('#tPjName').trigger('focus'); return $err.text('프로젝트명을 입력하세요.'); }
        const $btn = $('#tPjSave').prop('disabled', true);
        try {
            const cur = this.projects.find(x => x.num === +num) || {};
            const gName = $('#tPjGroup').val().trim();
            const pjName = name === cur.name ? name : await this._reviewName('project', name, { group: gName, num: +num });
            if (pjName === null) { $btn.prop('disabled', false); return $('#tPjName').trigger('focus'); }
            $('#tPjName').val(pjName);
            // 처음 보는 상위 이름이면 서버가 새로 만든다 — 그 전에 상위 이름도 본다
            let pgName = gName;
            if (gName && !this.groups.some(g => g.pg_name.replace(/\s/g, '') === gName.replace(/\s/g, ''))) {
                pgName = await this._reviewName('group', gName);
                if (pgName === null) { $btn.prop('disabled', false); return $('#tPjGroup').trigger('focus'); }
                $('#tPjGroup').val(pgName);
            }
            const r = await $.post('/adm/ajax/project/master/project-rename', { pj_num: num, pj_name: pjName, pg_name: pgName });
            $btn.prop('disabled', false);
            if (!r.result) return $err.text(r.message || '고치지 못했습니다.');
            const p = r.project;
            const i = this.projects.findIndex(x => x.num === p.num);
            if (i >= 0) this.projects[i] = p; else this.projects.push(p);
            $('#tProject option[value="' + p.num + '"]').text(p.label);
            $('#tProject').trigger('change');   // 검색칸 글자도 새 이름으로
            if (p.group_num && !this.groups.some(g => g.num === p.group_num)) {   // 수정하며 새로 생긴 상위
                this.groups.push({ num: p.group_num, pg_name: p.group });
            }
            $('#tPjAdd').hide();
            $j.Ui.Toast.show('프로젝트를 고쳤습니다');
            this._load();
        } catch (e) {
            $btn.prop('disabled', false);
            $err.text('고치지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
    }

    async _saveTask(t, phases) {
        const $err = $('#tErr').text('');
        const owners = $('#mpTaskForm [name=tOwners]:checked').map((_, i) => i.value).get();
        const ms = this._isMsPhases(phases);
        const cs = $('#tCS').val(), ce = ms ? cs : $('#tCE').val();
        if (!$('#tProject').val()) { $('#tProjectQ').trigger('focus'); return $err.text('프로젝트를 고르세요. 없으면 [추가]로 등록하세요.'); }
        if (!phases.length) return $err.text('구분을 하나 이상 고르세요.');
        if (!owners.length) return $err.text('담당자를 한 명 이상 고르세요.');
        if (!cs || !ce) return $err.text('날짜를 입력하세요.');
        if (ce < cs) return $err.text('종료일이 시작일보다 빠릅니다.');

        const body = {
            num: t ? t.num : '', pj_num: $('#tProject').val(), mt_name: $('#tName').val().trim(), phases, owner_nums: owners,
            mt_priority: $('#tPri').val(), mt_status: $('#tStatus').val(), mt_progress: $('#tProg').val(),
            deps: $('#mpTaskForm [name=tDeps]:checked').map((_, i) => i.value).get(),
            mt_memo: $('#tMemo').val().trim(), cur_start: cs, cur_end: ce,
        };
        if (t) {
            body.sched_mode = $('#mpTaskForm [name=tMode]:checked').val() || '';
            body.reason = ($('#tReason').val() || '').trim();
            const moved = cs !== t.mt_cur_start || ce !== t.mt_cur_end;
            if (moved && !body.sched_mode) return $err.text('일정을 바꿨습니다. 단순 수정인지 일정 지연·변경인지 고르세요.');
            if (moved && body.sched_mode === 'delay' && !body.reason) { $('#tReason').trigger('focus'); return $err.text('변경 사유를 입력하세요.'); }
        }

        const $btn = $('#mpTaskForm button[type=submit]').prop('disabled', true).text('저장 중…');
        try {
            const r = await $.post('/adm/ajax/project/master/save', body);
            if (!r.result) { $btn.prop('disabled', false).text('저장'); return $err.text(r.message || '저장하지 못했습니다.'); }
            this.sheet.close();
            $j.Ui.Toast.show(t ? '저장했습니다' : '작업을 추가했습니다');
            this._load();
        } catch (e) {
            $btn.prop('disabled', false).text('저장');
            $err.text('저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
    }

    async _deleteTask(t) {
        try {
            const r = await $.post('/adm/ajax/project/master/delete', { num: t.num });
            if (!r.result) return $('#tErr').text(r.message || '삭제하지 못했습니다.');
            this.sheet.close();
            $j.Ui.Toast.show('삭제했습니다');
            this._load();
        } catch (e) {
            $('#tErr').text('삭제하지 못했습니다.');
        }
    }
}
